import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Paper, Tab, Table, TableBody,
  TableCell, TableContainer, TableHead, TablePagination, TableRow, Tabs, Typography,
} from '@mui/material';
import axios from '../../utils/axios';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';

const statuses = { pending: 'Ожидают решения', approved: 'Одобренные', rejected: 'Отклонённые' };
const formatDate = value => value ? new Date(value).toLocaleString('ru-RU') : '—';

export default function PrivateBotRequests() {
  const { currentUser } = useAuth();
  const { hasPermission, loading: permissionsLoading } = usePermissions();
  const canView = !permissionsLoading && ['operator', 'curator'].includes(currentUser?.role)
    && hasPermission('telegramBots', 'view');
  const canEdit = canView && hasPermission('telegramBots', 'edit');
  const [status, setStatus] = useState('pending');
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);
  const [requests, setRequests] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [decisionError, setDecisionError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [reload, setReload] = useState(0);
  const busyRef = useRef(false);
  const sessionRef = useRef(0);
  const sequenceRef = useRef(0);

  useEffect(() => {
    sessionRef.current += 1;
    busyRef.current = false;
    setBusy(false);
    setDecisionError('');
    setNotice('');
    return () => { sessionRef.current += 1; };
  }, [canView, currentUser?._id, currentUser?.role]);

  useEffect(() => {
    let active = true;
    setRequests([]);
    setTotal(0);
    setLoadError('');
    setLoading(canView);
    if (!canView) return undefined;
    const load = async () => {
      if (busyRef.current) return;
      const sequence = ++sequenceRef.current;
      try {
        const { data } = await axios.get('/telegram-bots/access-requests', {
          params: { status, page: page + 1, limit },
        });
        if (!active || sequence !== sequenceRef.current) return;
        const lastPage = Math.max(0, Math.ceil(data.total / limit) - 1);
        if (page > lastPage) {
          setPage(lastPage);
          return;
        }
        setRequests(data.requests);
        setTotal(data.total);
        setLoadError('');
      } catch (error) {
        if (active && sequence === sequenceRef.current) {
          setLoadError(error.response?.data?.message || 'Не удалось загрузить заявки');
        }
      } finally {
        if (active && sequence === sequenceRef.current) setLoading(false);
      }
    };
    load();
    const interval = setInterval(load, 30000);
    return () => {
      active = false;
      sequenceRef.current += 1;
      clearInterval(interval);
    };
  }, [canView, currentUser?._id, currentUser?.role, status, page, limit, reload]);

  const decide = async (request, nextStatus) => {
    if (!canEdit || busyRef.current || loading || loadError || request.status !== 'pending') return;
    busyRef.current = true;
    sequenceRef.current += 1;
    setBusy(true);
    setDecisionError('');
    setNotice('');
    const session = sessionRef.current;
    try {
      await axios.patch(`/telegram-bots/${encodeURIComponent(request.botId)}/access-requests/${encodeURIComponent(request.userId)}`, { status: nextStatus });
      window.dispatchEvent(new CustomEvent('private-bot-requests-changed', { detail: { resolved: true } }));
      if (session !== sessionRef.current) return;
      setNotice(nextStatus === 'approved' ? 'Заявка одобрена' : 'Заявка отклонена');
      setReload(value => value + 1);
    } catch (error) {
      if (error.response?.status === 409) {
        window.dispatchEvent(new CustomEvent('private-bot-requests-changed'));
      }
      if (session !== sessionRef.current) return;
      setDecisionError(error.response?.data?.message || (error.response?.status === 409
        ? 'Заявка уже обработана. Список обновлён.' : 'Не удалось обработать заявку. Попробуйте ещё раз.'));
      if (error.response?.status === 409) setReload(value => value + 1);
    } finally {
      if (session === sessionRef.current) {
        busyRef.current = false;
        setBusy(false);
      }
    }
  };

  if (permissionsLoading) return <CircularProgress aria-label="Проверка прав" />;
  if (!canView) return <Alert severity="error">Нет прав для просмотра заявок</Alert>;

  return (
    <Box>
      <Typography variant="h4" component="h1" sx={{ mb: 2 }}>Заявки в приватные боты</Typography>
      <Tabs value={status} onChange={(_, value) => { setStatus(value); setPage(0); setDecisionError(''); setNotice(''); }} aria-label="Статус заявок" variant="scrollable" sx={{ mb: 2 }}>
        {Object.entries(statuses).map(([value, label]) => <Tab key={value} value={value} label={label} disabled={busy} />)}
      </Tabs>
      <Button onClick={() => setReload(value => value + 1)} disabled={loading || busy} sx={{ mb: 2 }}>Обновить список</Button>
      {notice && <Alert severity="success" sx={{ mb: 2 }}>{notice}</Alert>}
      {decisionError && <Alert severity="error" sx={{ mb: 2 }}>{decisionError}</Alert>}
      {loadError && <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" onClick={() => setReload(value => value + 1)} disabled={busy}>Повторить</Button>}>{loadError}</Alert>}
      {busy && <Typography role="status" sx={{ mb: 2 }}>Сохранение решения…</Typography>}
      {loading && <Box role="status" sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 3 }}><CircularProgress size={24} />Загрузка заявок…</Box>}
      {!loading && !loadError && (
        <Paper>
          <TableContainer>
            <Table aria-label="Заявки на доступ">
              <TableHead>
                <TableRow>
                  <TableCell>Бот</TableCell>
                  <TableCell>Заявитель</TableCell>
                  <TableCell>Telegram ID</TableCell>
                  <TableCell>Дата заявки</TableCell>
                  <TableCell>{status === 'pending' ? 'Решение' : 'Дата решения'}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {requests.map(request => (
                  <TableRow key={`${request.botId}:${request.userId}`}>
                    <TableCell>{request.botUsername ? `@${request.botUsername}` : request.botId}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{request.username ? `@${request.username}` : 'Без username'}</Typography>
                      <Typography variant="body2" color="text.secondary">{[request.firstName, request.lastName].filter(Boolean).join(' ') || '—'}</Typography>
                    </TableCell>
                    <TableCell>{request.userId}</TableCell>
                    <TableCell>{formatDate(request.createdAt)}</TableCell>
                    <TableCell>
                      {request.status === 'pending' ? canEdit ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          <Button size="small" color="success" disabled={busy} onClick={() => decide(request, 'approved')}>Одобрить</Button>
                          <Button size="small" color="error" disabled={busy} onClick={() => decide(request, 'rejected')}>Отклонить</Button>
                        </Box>
                      ) : 'Только просмотр' : formatDate(request.reviewedAt)}
                    </TableCell>
                  </TableRow>
                ))}
                {requests.length === 0 && <TableRow><TableCell colSpan={5} align="center">Заявок нет</TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div" count={total} page={page} rowsPerPage={limit}
            onPageChange={(_, value) => { if (!busy) setPage(value); }}
            onRowsPerPageChange={event => { if (!busy) { setLimit(Number(event.target.value)); setPage(0); } }}
            rowsPerPageOptions={[25, 50, 100]} labelRowsPerPage="Строк на странице:"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} из ${count}`}
            getItemAriaLabel={type => type === 'next' ? 'Следующая страница' : 'Предыдущая страница'}
            disabled={busy}
          />
        </Paper>
      )}
    </Box>
  );
}
